import { useState, type ReactNode } from 'react';
import type {
  FeatureState,
  OperatorAuditEntry,
  OperatorPlan,
  OperatorTenant,
} from '@forecastify/shared';
import { CONTROL_PANEL_PATHS } from '@forecastify/shared';
import { Button, Icon, cn } from '@forecastify/ui';
import { AUDIT_ACTION_LABEL, labelFor } from '../audit-labels';
import { useI18n, useT } from '../i18n';
import { useOperatorSession } from '../session';
import { formatDate, formatDateTime, formatMoney, formatNumber } from '../chrome/format';
import { useToast } from '../chrome/toasts';
import { BTN_SM, EYEBROW } from '../chrome/tones';
import { featureName } from './feature-names';
import type { TabId } from './tenant-detail';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Áttekintés* — five figures, the person to write to, and the way through. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MOCKUP'S FIVE CARDS, ITS CONTACT CARD, ITS HATCHED "BEYOND THE BOUNDARY" STRIP AND
 * ITS THREE SUMMARY ROWS — IN THAT ORDER.** The hatched strip is the boundary drawn as a *place*:
 * *Projektek, feladatok, munkaidő — a határon túl.* A console that simply lacks the task count reads
 * as unfinished; one that draws where the count would be and says nobody sees it reads as designed.
 *
 * ⚠️ **An invited organisation has no contact yet, so it shows who it is waiting for instead** —
 * the same card, a different person: the invited address, when the link dies, and *resend*.
 */
export function TenantOverview({
  tenant,
  plan,
  flags,
  trail,
  onGoToTab,
  onChanged,
  onContactReset,
}: {
  readonly tenant: OperatorTenant;
  readonly plan: OperatorPlan | undefined;
  readonly flags: readonly FeatureState[] | null;
  readonly trail: readonly OperatorAuditEntry[] | 'failed' | null;
  readonly onGoToTab: (tab: TabId) => void;
  readonly onChanged: () => void;
  readonly onContactReset: () => void;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const over = tenant.seatLimit !== null && tenant.seatsUsed > tenant.seatLimit;

  const metrics: readonly { label: string; value: string; sub: string; alarm?: boolean }[] = [
    { label: t('tenant.workspaces'), value: formatNumber(tenant.workspaceCount, locale), sub: '' },
    {
      label: t('tenant.members'),
      value: formatNumber(tenant.memberCount, locale),
      sub: tenant.seatsPending > 0 ? t('tenant.seats.pending', { count: tenant.seatsPending }) : '',
    },
    {
      label: t('tenant.seats'),
      value: `${formatNumber(tenant.seatsUsed, locale)} / ${
        tenant.seatLimit === null ? '∞' : formatNumber(tenant.seatLimit, locale)
      }`,
      sub: over
        ? t('tenant.seats.overShort')
        : tenant.seatLimitOverride === null
          ? t('tenant.seats.fromPlan')
          : t('tenant.seats.fromOverride'),
      alarm: over,
    },
    {
      label: t('danger.monthly'),
      value:
        tenant.monthlyValue === null || plan === undefined
          ? '–'
          : formatMoney(tenant.monthlyValue, plan.currency, locale),
      sub:
        tenant.status === 'trial' && tenant.trialEndsAt !== null
          ? t('tenant.trialUntil', { date: formatDate(tenant.trialEndsAt, locale) })
          : tenant.status === 'suspended'
            ? t('tenant.notBilling')
            : '',
    },
    { label: t('tenant.lastRequest'), value: ago(tenant.lastRequestAt, t), sub: '' },
  ];

  const overrides = (flags ?? []).filter((flag) => flag.source === 'override');
  const latest = trail === null || trail === 'failed' ? undefined : trail[0];

  const summary: readonly { label: string; value: string; tab: TabId }[] = [
    {
      label: t('tenant.plan'),
      value:
        tenant.planName === null
          ? t('tenant.plan.none')
          : plan !== undefined && !plan.isActive
            ? `${tenant.planName} · ${t('tenants.withdrawn')}`
            : tenant.planName,
      tab: 'plan',
    },
    {
      label: t('tenant.overrides'),
      value:
        flags === null
          ? '–'
          : overrides.length === 0
            ? t('tenant.overrides.none')
            : overrides
                .map(
                  (flag) =>
                    `${featureName(flag.key, t)}: ${flag.enabled ? t('feature.on') : t('feature.off')}`,
                )
                .join(', '),
      tab: 'features',
    },
    {
      label: t('tenant.auditLede'),
      value:
        latest === undefined
          ? '–'
          : `${labelFor(AUDIT_ACTION_LABEL, latest.action, t)} · ${formatDateTime(
              latest.createdAt,
              locale,
              {
                today: t('when.today'),
                yesterday: t('when.yesterday'),
              },
            )}`,
      tab: 'audit',
    },
  ];

  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
        {metrics.map((metric) => (
          <div key={metric.label} className="flex min-w-0 flex-col gap-1 rounded-lg border p-3">
            <span className="text-muted-foreground truncate text-[12px]">{metric.label}</span>
            <span
              className={cn(
                'text-[20px] leading-[1.2] font-semibold tracking-[-.015em] tabular-nums',
                metric.alarm === true ? 'text-destructive' : '',
              )}
            >
              {metric.value}
            </span>
            <span className="text-muted-foreground truncate text-[11.5px]">{metric.sub}</span>
          </div>
        ))}
      </div>

      {tenant.invitedEmail !== null ? (
        <PendingInvitation tenant={tenant} onChanged={onChanged} />
      ) : tenant.contactName !== null ? (
        <ContactCard tenant={tenant} onReset={onContactReset} />
      ) : null}

      <div
        className="flex items-center gap-3 rounded-lg border border-dashed p-3"
        style={{
          background:
            'repeating-linear-gradient(135deg,transparent 0 7px,color-mix(in oklab,var(--muted-foreground) 9%,transparent) 7px 8px)',
        }}
      >
        <span className="bg-background grid size-8 shrink-0 place-items-center rounded-md border">
          <Icon name="shield-check" size={15} className="text-muted-foreground" />
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="text-[13px] font-medium">{t('tenant.beyond.title')}</span>
          <span className="text-muted-foreground text-[12px]">{t('tenant.beyond.sub')}</span>
        </div>
      </div>

      <div className="flex flex-col rounded-lg border">
        {summary.map((row) => (
          <button
            key={row.label}
            type="button"
            onClick={() => {
              onGoToTab(row.tab);
            }}
            className="hover:bg-muted/50 flex min-h-10 items-center gap-3 border-b px-3.5 py-2 text-left last:border-0"
          >
            <span className="text-muted-foreground w-[150px] shrink-0 text-[12.5px]">
              {row.label}
            </span>
            <span className="min-w-0 flex-1 text-[13px]">{row.value}</span>
            <Icon name="chevron-right" size={14} className="text-muted-foreground shrink-0" />
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * ⚠️⚠️ **THE ONE PERSON AN OPERATOR MAY SEE, AND THE ONE THING THEY MAY DO FOR THEM.** The contact
 * crosses the boundary through `tenant_counts` (S-16); the reset is the support call it exists for —
 * a customer's own admin locked out. The address it goes to is the server's, never a field here.
 */
function ContactCard({
  tenant,
  onReset,
}: {
  readonly tenant: OperatorTenant;
  readonly onReset: () => void;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const name = tenant.contactName ?? '';

  return (
    <section
      aria-label={t('tenant.contact')}
      className="flex flex-wrap items-center gap-3 rounded-lg border p-4"
    >
      <span className="bg-primary/15 text-primary grid size-9 shrink-0 place-items-center rounded-full text-[12px] font-semibold">
        {initials(name)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn('text-muted-foreground', EYEBROW)}>{t('tenant.contact')}</span>
        <span className="text-[14px] font-medium">{name}</span>
        {tenant.contactEmail === null ? null : (
          <a
            href={`mailto:${tenant.contactEmail}`}
            className="text-muted-foreground hover:text-foreground truncate text-[12.5px]"
          >
            {tenant.contactEmail}
          </a>
        )}
        <span className="text-muted-foreground text-[12px]">
          {tenant.contactSince === null
            ? ''
            : `${t('tenant.contact.since', { date: formatDate(tenant.contactSince, locale, true) })} · ${
                tenant.contactLastSeenAt === null
                  ? t('tenant.contact.never')
                  : t('tenant.contact.lastSeen', {
                      when: formatDateTime(tenant.contactLastSeenAt, locale, {
                        today: t('when.today'),
                        yesterday: t('when.yesterday'),
                      }),
                    })
              }`}
        </span>
      </div>
      <Button variant="outline" size="sm" className={BTN_SM} onClick={onReset}>
        <Icon name="key" size={13} />
        {t('tenant.contact.reset')}
      </Button>
    </section>
  );
}

/** The invited organisation's card: who was asked, when the link dies, and *resend*. */
function PendingInvitation({
  tenant,
  onChanged,
}: {
  readonly tenant: OperatorTenant;
  readonly onChanged: () => void;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const expiresAt = tenant.invitedExpiresAt;
  /* `0202` · the link was used and the workspace not yet made: *awaiting setup*, not *invited*. */
  const acceptedAt = tenant.setupPendingSince;
  /* ⚠️ An expired link is a different state from a pending one: one is waiting, the other is dead
     until somebody resends. */
  const expired = expiresAt !== null && new Date(expiresAt).getTime() <= Date.now();

  const resend = (): void => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    void session
      .request<unknown>(CONTROL_PANEL_PATHS.resendTenantInvitation(tenant.id), { method: 'POST' })
      .then((result) => {
        setBusy(false);
        if (!result.ok) {
          if (result.status !== 401) setFailed(true);
          return;
        }
        toast({ title: t('tenant.pending.sent'), description: tenant.invitedEmail ?? '' });
        onChanged();
      });
  };

  return (
    <section
      aria-label={t('tenant.pending.title')}
      className="flex flex-wrap items-center gap-3 rounded-lg border p-4"
    >
      <span className="bg-muted text-muted-foreground grid size-9 shrink-0 place-items-center rounded-full">
        <Icon name="mail" size={15} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn('text-muted-foreground', EYEBROW)}>
          {acceptedAt === null ? t('tenant.pending.title') : t('tenant.pending.setupTitle')}
        </span>
        <span className="text-[14px] font-medium">{tenant.invitedEmail}</span>
        <span
          className={cn(
            'text-[12px]',
            expired ? 'font-medium text-[var(--status-warn-text)]' : 'text-muted-foreground',
          )}
        >
          {acceptedAt !== null
            ? t('tenant.pending.acceptedOn', { date: formatDate(acceptedAt, locale, true) })
            : expiresAt === null
              ? ''
              : expired
                ? t('tenant.pending.expiredOn', { date: formatDate(expiresAt, locale, true) })
                : t('tenant.pending.expiresOn', { date: formatDate(expiresAt, locale, true) })}
        </span>
        {failed ? (
          <span role="alert" className="text-destructive text-[12px]">
            {t('tenant.pending.failed')}
          </span>
        ) : null}
      </div>
      <Button variant="outline" size="sm" className={BTN_SM} disabled={busy} onClick={resend}>
        <Icon name="send-horizontal" size={13} />
        {busy ? t('tenant.pending.working') : t('tenant.pending.resend')}
      </Button>
    </section>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('') || '?'
  ).toUpperCase();
}

/** *4 perce* · *2 órája* · *3 napja* — the question is *still here?*, not *on what date*. */
function ago(at: string | null, t: ReturnType<typeof useT>): string {
  if (at === null) return t('tenant.lastRequest.never');
  const minutes = Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 60_000));
  if (minutes < 60) return t('tenant.ago.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t('tenant.ago.hours', { count: hours });
  return t('tenant.ago.days', { count: Math.round(hours / 24) });
}
